import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';

const Input = forwardRef(({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required = false,
  disabled = false,
  className = '',
  icon: Icon,
  iconPosition = 'left',
  size = 'medium',
  ...props
}, ref) => {
  const sizes = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-3 text-base',
    large: 'px-5 py-4 text-lg'
  };

  const inputClasses = `
    w-full
    ${sizes[size]}
    border rounded-xl
    bg-white/70 backdrop-blur-sm
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2
    hover:border-pragyavan-gold/40 hover:bg-white/90
    disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60
    placeholder:text-warm-gray/50
    ${error
      ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
      : 'border-warm-gray/20 focus:border-pragyavan-gold focus:ring-pragyavan-gold/20 focus:bg-white'
    }
    ${Icon && iconPosition === 'left' ? 'pl-12' : ''}
    ${Icon && iconPosition === 'right' ? 'pr-12' : ''}
    ${className}
  `.trim();

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-pragyavan-brown mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {Icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-warm-gray" />
          </div>
        )}

        <motion.input
          ref={ref}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClasses}
          whileFocus={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          {...props}
        />

        {Icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-warm-gray" />
          </div>
        )}
      </div>

      {(error || helperText) && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2"
        >
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          {!error && helperText && (
            <p className="text-sm text-warm-gray">{helperText}</p>
          )}
        </motion.div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;